import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronUp, Star } from "lucide-react";
import {
  ShieldCheck,
  Scales,
  Buildings,
  Copyright,
  Gavel,
  UsersThree,
  TrendUp,
  Medal,
  Clock,
  ChatCircleDots,
  Check,
  X,
  ArrowUpRight,
  Sparkle,
  Handshake,
} from "@phosphor-icons/react";
import ContactForm from "../components/ContactForm";
import ContactModal from "../components/ContactModal";
import SEO from "../components/SEO";
import MobileSlider from "../components/MobileSlider";
import PricingTabsSection from "../components/PricingTabsSection";
import { useScrollReveal } from "../hooks/useScrollReveal";

const Reveal = ({ children, direction = "up", delay = 0, className = "" }) => {
  const [ref, visible] = useScrollReveal();
  const animClass =
    direction === "left"
      ? "anim-slide-left"
      : direction === "right"
        ? "anim-slide-right"
        : direction === "scale"
          ? "anim-scale-in"
          : "anim-fade-up";
  const delayClass = delay ? `anim-delay-${delay}` : "";
  return (
    <div
      ref={ref}
      className={`${visible ? `${animClass} ${delayClass}` : "anim-hidden"} max-w-full ${className}`}
    >
      {children}
    </div>
  );
};

const Home = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeFaq, setActiveFaq] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const services = [
    {
      icon: ShieldCheck,
      title: "Trademark Registration",
      description:
        "From comprehensive trademark searches to USPTO application filing, we manage every stage of protecting your brand throughout the United States.",
      link: "/services/trademark/register",
    },
    {
      icon: Copyright,
      title: "Copyright Registration",
      description:
        "Protect your original creations, including books, software, music, artwork, and digital content, with official federal copyright registration.",
      link: "/services/copyright/register",
    },
    {
      icon: Medal,
      title: "Patent Filing",
      description:
        "Secure legal rights for your innovations with professional utility, design, and provisional patent application services handled with precision.",
      link: "/services/patent/application",
    },
    {
      icon: Buildings,
      title: "Business Formation",
      description:
        "Establish your LLC, C Corporation, S Corporation, or Nonprofit organization with the proper legal structure from the very beginning.",
      link: "/services/company/llc",
    },
    {
      icon: Gavel,
      title: "Legal Consulting",
      description:
        "Receive professional guidance on business immigration, commercial transactions, privacy compliance, contracts, and partnership matters.",
      link: "/services/legal/immigration",
    },
    {
      icon: UsersThree,
      title: "Business Support Services",
      description:
        "From Amazon Brand Registry and tax consulting to bookkeeping, CFO support, and business compliance, we provide solutions to help your company grow.",
      link: "/services/additional/amazon-brand-registry",
    },
  ];

  const processSteps = [
    {
      number: "1",
      title: "Provide Your Information",
      description:
        "Share your business name, logo, slogan, or invention with us. Our quick application process takes only a few minutes to complete.",
      icon: Sparkle,
    },
    {
      number: "2",
      title: "Attorney Review & Submission",
      description:
        "Our legal professionals perform a detailed search, prepare your filing, and submit your application to the appropriate federal agency.",
      icon: ShieldCheck,
    },
    {
      number: "3",
      title: "Official Registration",
      description:
        "Receive your trademark registration certificate and enjoy nationwide legal protection for your intellectual property.",
      icon: Handshake,
    },
  ];

  const comparisonData = [
    {
      feature: "Comprehensive trademark search",
      us: true,
      diy: false,
      others: true,
    },
    {
      feature: "Attorney-prepared application",
      us: true,
      diy: false,
      others: true,
    },
    {
      feature: "Office action response included",
      us: true,
      diy: false,
      others: false,
    },
    {
      feature: "Dedicated named attorney",
      us: true,
      diy: false,
      others: false,
    },
    {
      feature: "Ongoing brand monitoring",
      us: true,
      diy: false,
      others: false,
    },
    { feature: "Money-back guarantee", us: true, diy: false, others: false },
    {
      feature: "Starting price",
      usText: "$45",
      diyText: "$250+",
      othersText: "$500+",
    },
  ];

  const row1 = [
    {
      name: "Natalie Brooks",
      role: "Marketing Manager, Sterling Digital Group",
      image: "https://randomuser.me/api/portraits/women/44.jpg",
      text: "The Trademark Facility team made the entire registration process simple and stress-free. They kept me informed from start to finish.",
    },
    {
      name: "Brandon Lewis",
      role: "Founder, Elevate Apparel",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      text: "My Amazon Brand Registry was completed along with my trademark filing much faster than I expected. Excellent service.",
    },
    {
      name: "Jason Miller",
      role: "Product Director, Vertex Innovations",
      image: "https://randomuser.me/api/portraits/men/67.jpg",
      text: "Their patent specialists handled everything professionally. I appreciated the clear communication and attention to detail.",
    },
    {
      name: "Grace Mitchell",
      role: "Author & Publisher",
      image: "https://randomuser.me/api/portraits/women/68.jpg",
      text: "Copyright registration for my publications was completed smoothly. The team was knowledgeable and always available to help.",
    },
    {
      name: "Nathan Cooper",
      role: "Restaurant Owner",
      image: "https://randomuser.me/api/portraits/men/85.jpg",
      text: "A business started using a name similar to mine, but my registered trademark made resolving the issue straightforward.",
    },
  ];
  const row2 = [
    {
      name: "Victoria Hayes",
      role: "Founder, Luxe Cart",
      image: "https://randomuser.me/api/portraits/women/65.jpg",
      text: "My trademark application was submitted quickly, and I received updates throughout every stage of the process.",
    },
    {
      name: "Tyler Bennett",
      role: "Mobile App Developer",
      image: "https://randomuser.me/api/portraits/men/45.jpg",
      text: "I had no experience with trademarks, but they explained everything clearly and handled all the paperwork for me.",
    },
    {
      name: "Madison Reed",
      role: "Boutique Owner",
      image: "https://randomuser.me/api/portraits/women/32.jpg",
      text: "Protecting my boutique's name was incredibly easy with Trademark Facility. Their support exceeded my expectations.",
    },
    {
      name: "Christopher Allen",
      role: "CEO, Horizon Hospitality Group",
      image: "https://randomuser.me/api/portraits/men/72.jpg",
      text: "We filed several trademarks for our growing business, and every application was handled with professionalism and care.",
    },
    {
      name: "Samantha Foster",
      role: "Fashion Designer",
      image: "https://randomuser.me/api/portraits/women/50.jpg",
      text: "Affordable pricing, responsive support, and outstanding legal guidance. I highly recommend Trademark Facility.",
    },
  ];

  const faqs = [
    {
      question: "What can I protect with a trademark?",
      answer:
        "A trademark protects the unique identity of your business, including names, logos, slogans, and other distinctive branding elements. It helps prevent others from using similar marks that could confuse your customers.",
    },
    {
      question:
        "How long does the trademark registration process usually take?",
      answer:
        "The registration timeline depends on the USPTO review process and whether any issues arise during examination. Most applications take several months to complete, but we ensure your filing is submitted accurately and as quickly as possible.",
    },
    {
      question: "Does your pricing include USPTO filing fees?",
      answer:
        "No. Our service fees cover professional preparation and filing assistance. USPTO filing fees are government charges and must be paid separately at the time your application is submitted.",
    },
    {
      question: "What if my trademark application is refused?",
      answer:
        "If your application receives a refusal or office action, our legal team will review the issue and recommend the best solution. Depending on your service package, we'll assist you in responding to the USPTO to improve your chances of approval.",
    },
    {
      question: "Can one trademark cover multiple products or services?",
      answer:
        "Yes. A single trademark can be filed under multiple trademark classes if it is used for different products or services. Our attorneys will help determine the correct classes based on your business.",
    },
    {
      question: "How long is a registered trademark valid?",
      answer:
        "A registered trademark can remain protected indefinitely as long as you continue using it in commerce and file the required maintenance documents with the USPTO at the appropriate deadlines.",
    },
  ];

  const TestimonialCard = ({ t }) => (
    <div className="relative bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6 w-[300px] sm:w-[380px] shrink-0 transition-all duration-300 hover:bg-white/10 hover:border-lime/30 group">
      <div className="absolute top-3 right-5 font-heading text-4xl sm:text-5xl font-extrabold text-lime/10 leading-none select-none group-hover:text-lime/20 transition-colors duration-300">
        "
      </div>
      <div className="flex items-center gap-1 mb-3">
        {[...Array(5)].map((_, j) => (
          <Star
            key={j}
            className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-lime fill-lime"
          />
        ))}
      </div>
      <p className="text-gray-300 leading-relaxed text-xs sm:text-sm mb-4 relative z-10 line-clamp-3">
        "{t.text}"
      </p>
      <div className="flex items-center gap-3 pt-3 border-t border-white/10">
        <img
          src={t.image}
          alt={t.name}
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover ring-2 ring-lime/40"
        />
        <div>
          <div className="font-bold text-white text-xs sm:text-sm">
            {t.name}
          </div>
          <div className="text-lime text-[10px] sm:text-xs">{t.role}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen font-body overflow-x-hidden">
      <SEO
        title="Trademark Registration, Copyright & Patent Services"
        description="Trademark Facility offers affordable trademark registration starting at $45, copyright protection, patent filing, and LLC formation across the United States."
        path="/"
        keywords="trademark registration, copyright protection, patent filing, LLC formation, intellectual property, USPTO"
      />

      {/* ═══════ HERO ═══════ */}
      <section
        className="relative bg-primary overflow-hidden"
        data-testid="hero-section"
      >
        <img
          src="/assets/img/mix/main-banner-home.jpg"
          // src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&w=1920&h=1080&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/60 to-primary/70" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-24 lg:py-28 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-20 items-start overflow-hidden">
            {/* Form first on mobile */}
            <div className="lg:hidden w-full max-w-full overflow-hidden min-w-0">
              <ContactForm
                title={
                  <>
                    Protect your brand with{" "}
                    <span className="blink-price text-lime">trusted</span> legal
                    professionals.
                  </>
                }
                // subtitle="Protect your brand with trusted legal professionals."
                buttonText="Submit"
              />
            </div>
            <Reveal direction="left" className="min-w-0 w-full">
              <div className="inline-flex items-center gap-2 border border-lime/30 bg-lime/10 rounded-full px-3 py-1 sm:px-4 sm:py-1.5 mb-6 md:mb-8">
                <span className="w-2 h-2 rounded-full bg-lime animate-pulse" />
                <span className="text-[10px] sm:text-sm font-medium text-lime-300 tracking-wide">
                  Trusted by Businesses Nationwide
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-none mb-4 md:mb-6">
                Protect the Brand You've Worked Hard to Build.
              </h1>
              <p className="text-sm sm:text-lg text-gray-400 text-white leading-relaxed mb-6 md:mb-10">
                Trademark registration, copyright filing, patent protection, and
                business formation — managed by experienced intellectual
                property attorneys. Get started for as little as $45.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setIsModalOpen(true)}
                  data-testid="hero-cta-btn"
                  className="group inline-flex items-center justify-center bg-lime hover:bg-lime-light text-white font-bold py-2.5 px-5 rounded-xl transition-all duration-300 active:scale-95 text-sm sm:text-base"
                >
                  File My Trademark{" "}
                  <ArrowRight className="ml-2 h-4 w-4 text-white sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
                <a
                  href="tel:+18774050574"
                  className="inline-flex items-center justify-center border border-white hover:border-lime/50 text-gray-300 hover:text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300 text-sm sm:text-base"
                >
                  Call +1 (877) 405-0574
                </a>
              </div>
            </Reveal>
            <Reveal direction="right" className="hidden lg:block wd-70">
              <ContactForm
                title={
                  <>
                    {" "}
                    Protect your brand with{" "}
                    <span className="blink-price text-lime">trusted</span> legal
                    professionals.
                  </>
                }
                // subtitle="Protect your brand with trusted legal professionals."
                buttonText="Submit"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ SPLIT: WHY PROTECT — image first on mobile ═══════ */}
      <section
        className="bg-gray-50 py-14 md:py-28"
        data-testid="benefits-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-stretch overflow-hidden">
            <Reveal direction="left" className="flex">
              <img
                src="/assets/img/mix/Why-it-matters-home.png"
                alt="Brand protection consultation"
                className="w-full h-[250px] md:h-auto object-cover rounded-2xl"
              />
            </Reveal>
            <Reveal direction="right" className="flex flex-col justify-center">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em] text-center sm:text-left">
                Why Choose Trademark Protection
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mt-2 mb-4 text-center sm:text-left">
                Your brand deserves the strongest legal protection.
              </h2>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-6">
                Registering a federal trademark gives you exclusive rights to
                your brand and legal protection throughout all 50 states. It
                establishes your trademark as a valuable business asset,
                enhances your company's credibility, and builds confidence with
                customers and investors. Without official registration,
                protecting your brand against infringement can be far more
                difficult. Secure your intellectual property with Trademark
                Facility today.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="group inline-flex items-center bg-lime hover:bg-lime-light text-white font-bold py-2.5 px-5 rounded-xl transition-all duration-300 active:scale-95 text-sm self-center sm:self-start"
              >
                Protect My Brand{" "}
                <ArrowRight className="ml-2 h-4 w-4 text-white group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ HOW WE WORK — numbered cards matching /lp style ═══════ */}
      {/* <section
        className="bg-white py-14 md:py-28"
        data-testid="process-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <Reveal>
            <div className="text-center mb-10 md:mb-16">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Easy Process
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
                Just three simple steps to get started.
              </h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-0">
            {processSteps.map((step, i) => (
              <Reveal key={i} delay={i + 1}>
                <div
                  className={`relative p-8 sm:p-10 h-full text-center transition-all duration-500 hover:bg-primary hover:text-white group ${i < 2 ? "md:border-r border-b md:border-b-0 border-gray-200" : ""}`}
                  data-testid={`step-card-${i}`}
                >
                  <div className="w-14 h-14 rounded-full bg-primary text-lime font-heading text-2xl font-extrabold flex items-center justify-center mx-auto mb-5 group-hover:bg-lime group-hover:text-primary transition-colors duration-300">
                    {step.number}
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold mb-3 group-hover:text-white transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm group-hover:text-gray-300 transition-colors duration-300">
                    {step.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 md:mt-12 text-center">
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="process-cta-btn"
              className="group inline-flex items-center bg-primary hover:bg-primary-light text-white font-bold py-2.5 px-5 rounded-xl transition-all duration-300 active:scale-95 text-sm"
            >
              Start Your Application{" "}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </section> */}

      {/* ═══════ SPLIT: WHY CHOOSE US — image first on mobile ═══════ */}
      <section
        className="bg-primary py-14 md:py-28"
        data-testid="why-choose-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-stretch overflow-hidden">
            {/* Image first on mobile (order-1), text second (order-2). Desktop: text left (md:order-1), image right (md:order-2) */}
            <div className="order-2 md:order-1 flex flex-col justify-center">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em] text-center sm:text-left">
                Why Trademark Facility
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight mt-2 mb-4 text-center sm:text-left">
                Created for businesses committed to protecting their brand.
              </h2>
              <p className="text-gray-400 leading-relaxed text-sm sm:text-base mb-6">
                At Trademark Facility, we combine experienced legal guidance
                with personalized client support. Every application is reviewed
                and managed by a qualified attorney to ensure accuracy from
                start to finish. Our streamlined filing process, fast turnaround
                times, and high success rate help you secure protection without
                unnecessary delays. You'll receive dedicated assistance
                throughout your case, with direct access to our legal team
                whenever you need it. If we are unable to successfully process
                your application, you're covered by our money-back guarantee.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="group inline-flex items-center bg-lime hover:bg-lime-light text-white font-bold py-2.5 px-5 rounded-xl transition-all duration-300 active:scale-95 text-sm self-center sm:self-start"
              >
                Schedule a Free Consultation{" "}
                <ArrowRight className="ml-2 h-4 w-4 text-white group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
            <div className="order-1 md:order-2 flex">
              <img
                src="/assets/img/mix/sec-02.webp"
                alt="Expert team consultation"
                className="w-full h-[250px] md:h-auto object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ SERVICES — slider on mobile ═══════ */}
      <section
        className="bg-gray-50 py-14 md:py-28"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <Reveal>
            <div className="mb-10 md:mb-14">
              <span className="block text-xs font-bold text-lime uppercase tracking-[0.2em] text-center sm:text-left">
                Our Services
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mt-2 max-w-2xl text-center sm:text-left">
                Complete intellectual property and business solutions.
              </h2>
            </div>
          </Reveal>
          <MobileSlider enabled={isMobile}>
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className="group border border-gray-200 rounded-xl p-6 sm:p-8 h-full transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:border-lime/40"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-11 h-11 rounded-lg bg-lime/10 flex items-center justify-center mb-4 group-hover:bg-lime! transition-colors duration-300">
                    <Icon
                      size={22}
                      weight="duotone"
                      className="text-lime group-hover:text-white transition-colors duration-300"
                    />
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold mb-2">
                    {s.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm mb-4">
                    {s.description}
                  </p>
                  <Link
                    to={s.link}
                    className="inline-flex items-center text-lime font-semibold text-sm hover:text-lime-dark transition-colors duration-200"
                    data-testid={`service-link-${i}`}
                  >
                    Learn More
                    <ArrowUpRight size={16} weight="bold" className="ml-1" />
                  </Link>
                </div>
              );
            })}
          </MobileSlider>
        </div>
      </section>

      {/* ═══════ PRICING — tabbed self-filing / attorney-led ═══════ */}
      <PricingTabsSection onCtaClick={() => setIsModalOpen(true)} />

      {/* ═══════ COMPARISON ═══════ */}
      <section
        className="bg-gray-50 py-14 md:py-28"
        data-testid="comparison-section"
      >
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <Reveal>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                Compare
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#2d3132] tracking-tight mt-2">
                See how we stack up.
              </h2>
            </div>
          </Reveal>
          <Reveal direction="scale">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="comparison-table">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 pr-1 sm:pr-3 text-[10px] sm:text-sm font-bold text-gray-500 uppercase tracking-wider">
                      Feature
                    </th>
                    <th className="py-3 px-1 sm:px-4 text-center">
                      <span className="font-heading font-extrabold text-lime text-[11px] sm:text-lg">
                        Trademark Facility
                      </span>
                    </th>
                    <th className="py-3 px-1 sm:px-4 text-center text-[10px] sm:text-sm font-bold text-gray-500">
                      DIY
                    </th>
                    <th className="py-3 px-1 sm:px-4 text-center text-[10px] sm:text-sm font-bold text-gray-500">
                      Others
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-3 pr-1 sm:pr-3 text-[11px] sm:text-sm text-gray-700 font-medium leading-snug">
                        {row.feature}
                      </td>
                      <td className="py-3 px-1 sm:px-4 text-center">
                        {row.usText ? (
                          <span className="font-bold text-lime text-xs sm:text-sm">
                            {row.usText}
                          </span>
                        ) : row.us ? (
                          <Check
                            size={16}
                            weight="bold"
                            className="text-lime mx-auto"
                          />
                        ) : (
                          <X
                            size={16}
                            weight="bold"
                            className="text-gray-300 mx-auto"
                          />
                        )}
                      </td>
                      <td className="py-3 px-1 sm:px-4 text-center">
                        {row.diyText ? (
                          <span className="text-gray-500 text-[10px] sm:text-xs">
                            {row.diyText}
                          </span>
                        ) : row.diy ? (
                          <Check
                            size={16}
                            weight="bold"
                            className="text-green-500 mx-auto"
                          />
                        ) : (
                          <X
                            size={16}
                            weight="bold"
                            className="text-gray-300 mx-auto"
                          />
                        )}
                      </td>
                      <td className="py-3 px-1 sm:px-4 text-center">
                        {row.othersText ? (
                          <span className="text-gray-500 text-[10px] sm:text-xs">
                            {row.othersText}
                          </span>
                        ) : row.others ? (
                          <Check
                            size={16}
                            weight="bold"
                            className="text-green-500 mx-auto"
                          />
                        ) : (
                          <X
                            size={16}
                            weight="bold"
                            className="text-gray-300 mx-auto"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ TESTIMONIALS — marquee ═══════ */}
      <section
        className="bg-primary py-14 md:py-28 overflow-hidden"
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mb-10 md:mb-14">
          <Reveal>
            <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
              Testimonials
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight mt-2">
              What Our Clients Say
            </h2>
          </Reveal>
        </div>
        <div className="relative mb-5">
          <div className="flex marquee-right w-max gap-4 sm:gap-6">
            {[...row1, ...row1].map((t, i) => (
              <TestimonialCard key={i} t={t} />
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="flex marquee-left w-max gap-4 sm:gap-6">
            {[...row2, ...row2].map((t, i) => (
              <TestimonialCard key={i} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section className="bg-gray-50 py-14 md:py-28" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12">
          <Reveal>
            <span className="block text-xs font-bold text-lime uppercase tracking-[0.2em] text-center sm:text-left">
              FAQ
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#2d3132] tracking-tight mt-2 mb-8 md:mb-10  text-center sm:text-left">
              Answers to the questions we hear most often.
            </h2>
          </Reveal>
          <div className="divide-y divide-gray-200">
            {faqs.map((faq, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex justify-between items-center py-4 text-left group"
                >
                  <span className="font-heading font-bold text-[#2d3132] pr-4 text-sm sm:text-base group-hover:text-lime transition-colors duration-200">
                    {faq.question}
                  </span>
                  {activeFaq === i ? (
                    <ChevronUp className="h-5 w-5 text-lime shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                  )}
                </button>
                {activeFaq === i && (
                  <div className="pb-4 text-gray-600 leading-relaxed text-sm">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FINAL CTA ═══════ */}
      <section className="bg-primary py-14 md:py-28" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4 md:mb-6">
              Ready to Secure Your Brand?
            </h2>
            <p className="text-gray-400 text-sm sm:text-lg mb-8 md:mb-10 max-w-2xl mx-auto">
              Don't wait until someone else claims your identity. Register your
              trademark today and protect your business with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setIsModalOpen(true)}
                data-testid="final-cta-btn"
                className="group inline-flex items-center justify-center bg-lime text-white hover:bg-lime-light font-bold py-2.5 px-6 rounded-xl transition-all duration-300 hover:shadow-xl active:scale-95 text-sm sm:text-base"
              >
                Get Started — $45{" "}
                <ArrowRight className="ml-2 h-4 w-4 text-white sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <button
                onClick={() => {
                  if (window.LiveChatWidget)
                    window.LiveChatWidget.call("maximize");
                }}
                className="js-chat inline-flex items-center justify-center border-2 border-white/20 hover:border-lime/50 text-white hover:text-lime font-bold py-2.5 px-6 rounded-xl transition-all duration-300 text-sm sm:text-base"
              >
                Talk to an Attorney
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Get Started with Trademark Facility"
      />
    </div>
  );
};

export default Home;
